import { copyToClipboard } from "@elit-hair/elit-tools";
import { Button, Icon } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbCopy, TbPlus } from "react-icons/tb";
import { toast } from "sonner";
import { z } from "zod";

import { PageActions } from "@/components/page-actions";
import { SearchBar } from "@/components/ui/search-bar";
import { DIMENSION_DEFINITION_LIMIT } from "@/constants/dimensions";
import { useDimensionDefinitions } from "@/queries/dimension-definition.queries";
import type { DimensionDefinitionType } from "@/types/dimension-definition.types";
import { DEFAULT_DEBOUNCE_MS } from "@/utils/debounce";
import { serializeDimensionDefinitionsForClipboard } from "@/utils/dimension-definition.utils";
import { useUrlSyncedTanstackPagination } from "@/utils/pagination";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";
import { searchValidator } from "@/utils/search-validator";

import { DimensionDefinitionForm } from "./components/dimension-definition-form";
import { DimensionDefinitionPromptVersionsDialog } from "./components/dimension-definition-prompt-versions-dialog";
import { DimensionDefinitionTable } from "./components/dimension-definition-table";

const DimensionDefinitionsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(DIMENSION_DEFINITION_LIMIT).optional(),
    search: z.string().optional(),
});

export const Route = createFileRoute(APP_ROUTES_FULL.AI_DIMENSION_DEFINITIONS)({
    component: AIDimensionDefinitions,
    validateSearch: searchValidator(DimensionDefinitionsSearchSchema),
    staticData: {
        config: {
            breadcrumb: { title: "dimension_definitions", to: APP_ROUTES_TO.AI_DIMENSION_DEFINITIONS },
            title: "dimension_definitions",
        },
    },
});

function AIDimensionDefinitions() {
    const { t } = useTranslation();
    const navigate = Route.useNavigate();
    const searchParams = Route.useSearch({ structuralSharing: true });
    const { page, pageSize, pagination, onPaginationChange } = useUrlSyncedTanstackPagination({
        search: searchParams,
        navigate,
        to: Route.to,
        defaultPageSize: DIMENSION_DEFINITION_LIMIT,
    });

    const [formOpen, setFormOpen] = useState(false);
    const [editData, setEditData] = useState<DimensionDefinitionType | null>(null);
    const [historyDefinition, setHistoryDefinition] = useState<DimensionDefinitionType | null>(null);
    const [searchValue, setSearchValue] = useState(searchParams.search ?? "");

    useEffect(() => {
        setSearchValue(searchParams.search ?? "");
    }, [searchParams.search]);

    const { data, isLoading, isError, error, refetch } = useDimensionDefinitions({
        page,
        limit: pageSize,
        search: searchParams.search,
    });

    const pageDefinitions = data?.data ?? [];
    const totalCount = data?.pagination.total ?? 0;

    const updateSearch = useCallback(
        (nextSearch: string) => {
            navigate({
                to: Route.to,
                search: (prev) => ({
                    ...prev,
                    search: nextSearch || undefined,
                    page: undefined,
                }),
                replace: true,
            });
        },
        [navigate]
    );

    const handleAddNew = () => {
        setEditData(null);
        setFormOpen(true);
    };

    const handleEdit = (item: DimensionDefinitionType) => {
        setEditData(item);
        setFormOpen(true);
    };

    const handleFormClose = (open: boolean) => {
        setFormOpen(open);
        if (!open) {
            setEditData(null);
        }
    };

    const handleCopyPage = async () => {
        if (pageDefinitions.length === 0) return;
        await copyToClipboard(serializeDimensionDefinitionsForClipboard(pageDefinitions));
        toast.success(t("dimension_definitions_copied_to_clipboard", { count: pageDefinitions.length }));
    };

    if (isError) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center space-y-4">
                <p className="text-lg font-medium text-destructive">{t("error_loading_data")}</p>
                <p className="text-sm text-muted-foreground">{error?.message || t("something_went_wrong")}</p>
                <Button onClick={() => refetch()}>{t("try_again")}</Button>
            </div>
        );
    }

    return (
        <>
            <PageActions>
                <SearchBar
                    value={searchValue}
                    onChange={setSearchValue}
                    placeholder={t("search_dimension_definitions")}
                    debounceMs={DEFAULT_DEBOUNCE_MS}
                    minLength={0}
                    onDebouncedChange={(value) => {
                        if (value === (searchParams.search ?? "")) return;
                        updateSearch(value);
                    }}
                    className="sm:w-60"
                />
                <Button variant="outline" onClick={handleCopyPage} disabled={isLoading || pageDefinitions.length === 0}>
                    <Icon icon={TbCopy} />
                    {t("copy_page_dimension_definitions")}
                </Button>
                <Button onClick={handleAddNew}>
                    <Icon icon={TbPlus} />
                    {t("add_dimension_definition")}
                </Button>
            </PageActions>

            {searchParams.search?.trim() && !isLoading ? (
                <div className="border-y bg-muted/50 px-6 py-2 text-sm text-muted-foreground">
                    {pageDefinitions.length
                        ? t("search_results_found", {
                              count: totalCount,
                              term: searchParams.search.trim(),
                          })
                        : t("no_search_results", { term: searchParams.search.trim() })}
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto p-4 md:p-6">
                <DimensionDefinitionTable
                    data={pageDefinitions}
                    totalCount={totalCount}
                    isLoading={isLoading}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    onEdit={handleEdit}
                    onPromptHistory={setHistoryDefinition}
                />
            </div>

            <DimensionDefinitionForm open={formOpen} onOpenChange={handleFormClose} editData={editData} />
            <DimensionDefinitionPromptVersionsDialog
                definition={historyDefinition}
                onOpenChange={(open) => {
                    if (!open) setHistoryDefinition(null);
                }}
            />
        </>
    );
}
